<template>
  <div class="active-game" :class="`is-view-${view}`">
    <div class="play-column">
      <GameFinale
        v-if="gameStore.isGameOver"
        :key="gameStore.gameId"
        class="play-panel"
        :game-id="gameStore.gameId"
        :players="gameStore.players"
        :rounds="gameStore.rounds"
        @rematch="startRematch"
        @view-sheet="view = 'sheet'"
        @undo="undoRound"
      />

      <section v-else class="panel standings-panel" aria-labelledby="standings-title">
        <header class="panel-head">
          <h1 id="standings-title">Standings</h1>
          <p class="panel-meta">Round {{ gameStore.currentRound }} of {{ gameStore.maxRound }}</p>
        </header>
        <div
          class="progress"
          role="progressbar"
          :aria-valuenow="gameStore.rounds.length"
          aria-valuemin="0"
          :aria-valuemax="gameStore.maxRound"
          aria-label="Rounds played"
        >
          <span
            :style="{ width: `${(gameStore.rounds.length / gameStore.maxRound) * 100}%` }"
          ></span>
        </div>
        <Standings
          :players="gameStore.players"
          :rounds="gameStore.rounds"
          :current-player-index="gameStore.currentPlayerIndex"
        />
      </section>

      <div
        v-if="!gameStore.isGameOver"
        class="view-tabs segmented"
        role="tablist"
        aria-label="Game view"
      >
        <button
          type="button"
          role="tab"
          :aria-selected="view === 'play'"
          :class="{ 'is-active': view === 'play' }"
          @click="view = 'play'"
        >
          Play
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="view === 'sheet'"
          :class="{ 'is-active': view === 'sheet' }"
          @click="view = 'sheet'"
        >
          Scoresheet
        </button>
      </div>

      <section
        v-if="!gameStore.isGameOver"
        class="panel play-panel turn"
        aria-labelledby="turn-title"
      >
        <header class="turn-head">
          <h2 id="turn-title">{{ currentPlayerName }} picks a game</h2>
          <div class="history-buttons">
            <button
              type="button"
              class="icon-btn"
              :disabled="!gameStore.canUndo"
              :title="lastRoundLabel ? `Undo ${lastRoundLabel}` : 'Nothing to undo'"
              @click="undoRound"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 010 11H11"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Undo</span>
            </button>
            <button
              type="button"
              class="icon-btn"
              :disabled="!gameStore.canRedo"
              title="Redo the round you undid"
              @click="gameStore.redoLastRound()"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 000 11H13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Redo</span>
            </button>
          </div>
        </header>

        <button
          type="button"
          role="switch"
          class="double-switch"
          :aria-checked="isDouble"
          :disabled="isLastGame"
          @click="isDouble = !isDouble"
        >
          <span class="switch-track" aria-hidden="true"><span class="switch-thumb"></span></span>
          <span class="switch-text">
            <span class="switch-label">Double points</span>
            <span class="switch-hint">
              {{
                isLastGame
                  ? "A player's last game can't be doubled"
                  : 'Every score this round counts twice'
              }}
            </span>
          </span>
        </button>

        <div class="games-grid">
          <button
            v-for="game in gameStore.availableMiniGames"
            :key="game.id"
            type="button"
            class="game-tile"
            :disabled="isDouble && game.id === GAME_TYPES.RENTZ"
            @click="selectMiniGame(game)"
          >
            <GameIndex :symbol="game.symbol" />
            <span class="tile-text">
              <span class="tile-name">{{ game.displayName }}</span>
              <span class="tile-rule">
                {{
                  isDouble && game.id === GAME_TYPES.RENTZ
                    ? "Can't be doubled"
                    : ruleText(game, gameStore.players.length, isDouble ? 2 : 1)
                }}
              </span>
            </span>
          </button>
        </div>
      </section>

      <section v-if="!gameStore.isGameOver" class="panel play-panel" aria-labelledby="hands-title">
        <header class="panel-head hands-head">
          <h2 id="hands-title">Games left</h2>
          <p class="panel-meta">{{ roundsLeftLabel }}</p>
        </header>
        <GamesLeft
          :players="gameStore.players"
          :rounds="gameStore.rounds"
          :mini-games="gameStore.miniGames"
          :current-player-index="gameStore.currentPlayerIndex"
        />
      </section>
    </div>

    <aside class="sheet-column">
      <button
        v-if="gameStore.isGameOver"
        type="button"
        class="btn btn-secondary back-to-result"
        @click="view = 'play'"
      >
        Back to result
      </button>
      <section class="panel sheet-panel" aria-labelledby="sheet-title">
        <header class="panel-head sheet-head">
          <h2 id="sheet-title">Scoresheet</h2>
        </header>
        <ScoreSheet :players="gameStore.players" :rounds="gameStore.rounds" />
      </section>
    </aside>

    <Transition name="toast">
      <div v-if="toast" class="toast" role="status">
        <span>{{ toast }}</span>
        <button type="button" class="toast-action" @click="undoRound">Undo</button>
      </div>
    </Transition>

    <SingleCardInputModal
      :is-open="isInputOpen('card')"
      :game="pickedGame"
      :players="gameStore.players"
      :is-double="isDouble"
      @confirm="saveRound"
      @close="closeInput"
    />

    <RentzOrderModal
      :is-open="isInputOpen('order')"
      :players="gameStore.players"
      @confirm="saveRound"
      @close="closeInput"
    />

    <UnitsPerPlayerInputModal
      :is-open="isInputOpen('units')"
      :game="pickedGame"
      :players="gameStore.players"
      :is-double="isDouble"
      @confirm="saveRound"
      @close="closeInput"
    />

    <TotalsInputModal
      :is-open="isInputOpen('totals')"
      :players="gameStore.players"
      :is-double="isDouble"
      @confirm="saveRound"
      @close="closeInput"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useGameStore } from '../stores/game';
import { useGameConfigStore } from '../stores/gameConfig';
import { GAME_TYPES } from '../constants/gameTypes';
import { ruleText } from '../utils/gameRules';
import GameIndex from '../components/GameIndex.vue';
import GameFinale from '../components/GameFinale.vue';
import GamesLeft from '../components/GamesLeft.vue';
import ScoreSheet from '../components/ScoreSheet.vue';
import Standings from '../components/Standings.vue';
import SingleCardInputModal from '../components/SingleCardInputModal.vue';
import RentzOrderModal from '../components/RentzOrderModal.vue';
import UnitsPerPlayerInputModal from '../components/UnitsPerPlayerInputModal.vue';
import TotalsInputModal from '../components/TotalsInputModal.vue';

const router = useRouter();
const gameStore = useGameStore();
const gameConfig = useGameConfigStore();
const isDouble = ref(false);
const pickedGame = ref(null);
const isEnteringScores = ref(false);
const toast = ref('');
const view = ref('play');
let toastTimer = null;

if (gameStore.players.length === 0) {
  router.replace('/new-game');
} else if (gameStore.miniGames.length === 0) {
  router.replace('/select-mini-games');
}

const currentPlayerName = computed(() => gameStore.playerNames[gameStore.currentPlayerIndex]);

const roundsLeftLabel = computed(() => {
  const left = gameStore.maxRound - gameStore.rounds.length;
  return `${left} ${left === 1 ? 'round' : 'rounds'} to go`;
});

const isLastGame = computed(() => gameStore.availableMiniGames.length === 1);

watch(isLastGame, (last) => {
  if (last) isDouble.value = false;
});

const lastRoundLabel = computed(() => {
  const last = gameStore.rounds[gameStore.rounds.length - 1];
  if (!last) return '';
  const name = gameConfig.games[last.miniGame.id]?.displayName ?? last.miniGame.id;
  return `round ${gameStore.rounds.length} (${name})`;
});

const showToast = (message) => {
  clearTimeout(toastTimer);
  toast.value = message;
  toastTimer = setTimeout(() => {
    toast.value = '';
  }, 5000);
};

const hideToast = () => {
  clearTimeout(toastTimer);
  toast.value = '';
};

onBeforeUnmount(() => clearTimeout(toastTimer));

const isInputOpen = (input) => isEnteringScores.value && pickedGame.value?.input === input;

const selectMiniGame = (game) => {
  hideToast();
  pickedGame.value = game;
  isEnteringScores.value = true;
};

const closeInput = () => {
  isEnteringScores.value = false;
};

const saveRound = (roundScores) => {
  const game = pickedGame.value;
  const doubled = isDouble.value;
  closeInput();
  gameStore.addRoundScores(roundScores, game.id, doubled);
  isDouble.value = false;
  showToast(
    `Round ${gameStore.rounds.length} saved: ${game.displayName}${doubled ? ', doubled' : ''}`
  );
};

const undoRound = () => {
  hideToast();
  gameStore.undoLastRound();
};

const startRematch = () => {
  hideToast();
  isDouble.value = false;
  view.value = 'play';
  gameStore.startRematch();
  window.scrollTo({ top: 0 });
};
</script>

<style scoped>
.active-game {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}

.play-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.panel {
  padding: 1.25rem;
  border-radius: 14px;
  background-color: var(--panel-solid);
  border: 1px solid var(--panel-edge);
}

.panel h1,
.panel h2 {
  font-size: 1.375rem;
}

.hands-head {
  margin-bottom: 0.75rem;
}

.panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.panel-meta {
  color: var(--on-table-muted);
  font-size: var(--step--1);
  white-space: nowrap;
}

.standings-panel .progress {
  margin: 0.625rem 0 0.875rem;
}

.turn-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: -0.25rem 0 1rem;
}

.history-buttons {
  display: flex;
  gap: 0.25rem;
  flex: none;
}

.icon-btn {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.625rem;
  border-radius: var(--radius-control);
  color: var(--on-table);
  font-weight: 600;
  font-size: 0.875rem;
}

.icon-btn:hover:not(:disabled) {
  background-color: var(--table-raised);
}

.icon-btn:disabled {
  opacity: 0.35;
}

.progress {
  height: 4px;
  border-radius: 2px;
  background-color: var(--table-line);
  overflow: hidden;
}

.progress span {
  display: block;
  height: 100%;
  background-color: var(--on-table);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.double-switch {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.375rem 0;
  margin-bottom: 1rem;
  border-radius: var(--radius-control);
  text-align: left;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.double-switch[aria-checked='true'] .switch-label {
  color: var(--gold);
}

.double-switch:disabled {
  opacity: 0.55;
}

.switch-track {
  flex: none;
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  background-color: var(--table-line);
  transition: background-color 0.2s;
}

.switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--card);
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}

[aria-checked='true'] .switch-track {
  background-color: var(--gold);
}

[aria-checked='true'] .switch-thumb {
  transform: translateX(18px);
}

.switch-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.switch-label {
  font-weight: 600;
}

.switch-hint {
  font-size: var(--step--1);
  color: var(--on-table-muted);
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

@media (min-width: 560px) {
  .games-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.game-tile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 4.25rem;
  padding: 0.5rem 0.75rem 0.5rem 0.5rem;
  border-radius: var(--radius-card);
  background-color: var(--table-raised);
  border: 1px solid transparent;
  text-align: left;
  transition:
    border-color 0.15s,
    background-color 0.15s,
    transform 0.1s;
}

.game-tile:hover:not(:disabled) {
  background-color: var(--table-hover);
  border-color: var(--panel-edge);
}

.game-tile:active:not(:disabled) {
  transform: scale(0.98);
}

.game-tile:disabled {
  opacity: 0.4;
}

.tile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.tile-name {
  font-weight: 600;
}

.tile-rule {
  font-size: 0.8125rem;
  color: var(--on-table-muted);
}

.sheet-column {
  min-width: 0;
}

.back-to-result {
  width: 100%;
  margin-bottom: 1rem;
}

.sheet-head {
  margin-bottom: 0.75rem;
}

.sheet-panel {
  padding: 1.25rem 0.75rem 0.5rem;
}

.sheet-panel .sheet-head {
  padding: 0 0.5rem;
}

@media (max-width: 1023px) {
  .is-view-play .sheet-column,
  .is-view-sheet .play-panel {
    display: none;
  }

  .sheet-panel {
    padding-top: 0.5rem;
  }

  .sheet-head {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}

.toast {
  position: fixed;
  left: 50%;
  bottom: calc(1rem + var(--tabbar-space));
  transform: translateX(-50%);
  z-index: 900;
  width: max-content;
  max-width: calc(100vw - 32px);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-radius: 12px;
  background-color: var(--card);
  color: var(--ink);
  box-shadow: var(--shadow-lift);
  font-weight: 500;
}

.toast-action {
  min-height: 40px;
  padding: 0 0.875rem;
  border-radius: var(--radius-control);
  background-color: var(--strong);
  color: var(--on-strong);
  font-weight: 600;
}

.toast-action:focus-visible {
  outline-color: var(--strong);
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

@media (max-width: 419px) {
  .icon-btn {
    padding: 0 0.5rem;
  }

  .icon-btn span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  .game-tile {
    gap: 0.625rem;
    padding-right: 0.5rem;
  }

  .game-tile :deep(.game-index) {
    width: 2.125rem;
    height: 2.875rem;
    font-size: 1.25rem;
  }

  .panel {
    padding: 1rem;
  }
}

@media (min-width: 1024px) {
  .view-tabs,
  .back-to-result {
    display: none;
  }

  .active-game {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 1.75rem 2.5rem;
  }

  .sheet-column {
    position: sticky;
    top: calc(64px + 1.5rem);
    max-height: calc(100vh - 64px - 3rem);
    overflow-y: auto;
  }

  .play-column .panel {
    padding: 1.5rem;
  }

  .sheet-panel {
    padding: 1.5rem 0.75rem 0.5rem;
  }

  .sheet-panel .sheet-head {
    padding: 0 0.75rem;
  }
}
</style>
